import type { SingleProxy } from "@/api/custom_types";
import ImportWarningDialog from "@/components/CSV/ImportWarningDialog";
import { ReusableHotTable } from "@/components/CSV/ReusableHotTable";
import { useCsvExport, useCsvImport } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useBatchAddProxies } from "@/services";
import type { Row } from "@/utils/csv-helpers";
import {
	Button,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import CSVImportFileDialog from "./ImportFileDialog";
import {
	createEmptyRow,
	createProxyBatchItems,
	makeColumnSpecs,
	validateData,
} from "./helper";

type CSVCols = "ipAddress" | "port" | "username" | "password";

export type RowData = Row<CSVCols>;

export const columnsKey: (keyof RowData & string)[] = [
	"ipAddress",
	"port",
	"username",
	"password",
];

const ProxiesNewCsv: React.FC = () => {
	const { t } = useTranslation();
	const batchAddProxiesMutation = useBatchAddProxies();

	const initialRows: RowData[] = Array.from({ length: 200 }, () =>
		createEmptyRow(),
	);

	const [submitError, setSubmitError] = useState<string>("");

	const {
		data,
		openPipeline,
		continuePipeline,
		warning,
		fileDlg,
		setData,
		clearData,
	} = useCsvImport<RowData>(initialRows);

	const excelExport = useCsvExport<RowData>(data, columnsKey);

	const columns = useMemo(() => makeColumnSpecs(t), [t]);

	const [isDataValid, setIsDataValid] = useState<boolean>(true);

	const handleImportFile = useCallback(
		(rows: RowData[]) => {
			setData(rows);
			fileDlg.onClose();
		},
		[fileDlg.onClose, setData],
	);

	useEffect(() => {
		const { isValid, errorMessage } = validateData(data, t);
		setIsDataValid(isValid);
		if (!isValid) {
			setSubmitError(errorMessage);
		} else {
			setSubmitError("");
		}
	}, [data, t]);

	const submit = useCallback(() => {
		const batchItems: SingleProxy[] = createProxyBatchItems(data);

		batchAddProxiesMutation.mutate(
			{ body: { proxy_kind: "PERSONAL", proxies: batchItems } },
			{
				onSuccess: () => {
					clearData();
				},
				onError: (err) => toast.error((err as Error).message),
			},
		);
	}, [batchAddProxiesMutation, data, clearData]);

	const downloadTemplate = () => {
		const link = document.createElement("a");
		link.href = "/assets/files/proxies_data.csv";
		link.download = "proxies_data.csv";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<div className="pb-10">
			<div className="mb-2 flex flex-col items-center justify-between gap-4 md:flex-row">
				<Typography variant="title-md" as="h2" className="py-2">
					{t("proxy.new.csvTitle")}
				</Typography>

				<div className="flex h-auto flex-wrap justify-center gap-4">
					<Button
						type="button"
						color="secondary"
						size="sm"
						className="w-[80%] normal-case md:w-auto"
						onClick={downloadTemplate}
					>
						{t("proxy.new.csvButtonDownloadTemplate")}
					</Button>

					<Button
						type="button"
						color="secondary"
						size="sm"
						className="normal-case"
						onClick={openPipeline}
					>
						{t("proxy.new.csvButtonImportFile")}
					</Button>

					<Button
						type="button"
						color="secondary"
						size="sm"
						className="normal-case"
						onClick={() => excelExport("proxies_data")}
					>
						{t("proxy.new.csvButtonExportFile")}
					</Button>

					<Tooltip>
						<TooltipTrigger asChild>
							<span className="inline-flex">
								<Button
									type="button"
									color="primary"
									size="sm"
									className="px-10"
									loading={batchAddProxiesMutation.isPending}
									disabled={!isDataValid}
									onClick={submit}
								>
									{t("proxy.new.csvButtonSubmit")}
								</Button>
							</span>
						</TooltipTrigger>
						{!!submitError && (
							<TooltipContent>
								<p>{submitError}</p>
							</TooltipContent>
						)}
					</Tooltip>
				</div>
			</div>

			<ReusableHotTable<RowData>
				data={data}
				columns={columns}
				hotProps={{
					minRows: 200,
					stretchH: "all",
					afterChange: function (this: any, changes, source) {
						if (
							changes &&
							(source === "edit" ||
								source === "CopyPaste.paste" ||
								source === "updateData" ||
								source === "Autofill.fill")
						) {
							const updated = this.getSourceData() as RowData[];
							setData([...updated]);
						}
					},
				}}
			/>

			<CSVImportFileDialog
				open={fileDlg.open}
				onClose={fileDlg.onClose}
				onImport={handleImportFile}
			/>

			<ImportWarningDialog
				open={warning.open}
				onClose={warning.onClose}
				onContinue={continuePipeline}
			/>
		</div>
	);
};

export default ProxiesNewCsv;
